import { Line } from '@react-three/drei'
import { montanteColunaC, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import type { TipoEstruturaVeicular } from '../../dominio/didatico/tiposEstruturaVeicular'
import { Rotulo3D } from '../componentes/Rotulo3D'

interface Props {
  perfil: PerfilVeiculo
  tipo: TipoEstruturaVeicular
}

/** Esquemas sobrepostos ao mesmo carro para comparar o princípio estrutural. */
export function EstruturaComparativa({ perfil, tipo }: Props) {
  if (tipo === 'chassi') return <ChassiEscada perfil={perfil} />
  if (tipo === 'estruturaEspacial') return <EstruturaEspacial perfil={perfil} />
  return (
    <Rotulo3D
      posicao={[0, perfil.tetoY + 0.35, 0]}
      texto="MONOBLOCO · conjunto integrado"
      variante="estrutura"
      fator={5}
    />
  )
}

function ChassiEscada({ perfil }: { perfil: PerfilVeiculo }) {
  const x = perfil.largura * 0.29
  const y = 0.13
  const frente = perfil.frenteZ - 0.18
  const traseira = perfil.traseiraZ + 0.18
  const travessas = [perfil.eixoDianteiroZ, 0.65, -0.65, perfil.eixoTraseiroZ]
  return (
    <group>
      {[-x, x].map((lateral) => (
        <Line key={lateral} points={[[lateral, y, traseira], [lateral, y, frente]]} color="#58c8f7" lineWidth={10} />
      ))}
      {travessas.map((z) => (
        <Line key={z} points={[[-x, y, z], [x, y, z]]} color="#58c8f7" lineWidth={9} />
      ))}
      <Rotulo3D posicao={[0, perfil.tetoY + 0.35, 0]} texto="CHASSI SEPARADO · quadro tipo escada" variante="eixo" fator={5} />
    </group>
  )
}

function EstruturaEspacial({ perfil }: { perfil: PerfilVeiculo }) {
  const x = perfil.largura / 2 - 0.04
  const yBase = perfil.topoSoleiraY
  const yTopo = perfil.tetoY - 0.03
  const zFrenteBase = perfil.colunaA.zBase
  const zFrenteTopo = perfil.colunaA.zTopo
  // o nó traseiro inferior é o pé da coluna C, na soleira, antes da roda
  const zTrasBase = montanteColunaC(perfil).zPe
  const zTrasTopo = perfil.colunaC.zTopo
  const segmentos: Array<[[number, number, number], [number, number, number]]> = []
  for (const lado of [-1, 1]) {
    const lx = lado * x
    segmentos.push(
      [[lx, yBase, zTrasBase], [lx, yBase, zFrenteBase]],
      [[lx, yTopo, zTrasTopo], [lx, yTopo, zFrenteTopo]],
      [[lx, yBase, zFrenteBase], [lx, yTopo, zFrenteTopo]],
      [[lx, yBase, zTrasBase], [lx, yTopo, zTrasTopo]],
      [[lx, yBase, zTrasBase], [lx, yTopo, zFrenteTopo]],
      [[lx, yBase, zFrenteBase], [lx, yTopo, zTrasTopo]],
    )
  }
  segmentos.push(
    [[-x, yTopo, zFrenteTopo], [x, yTopo, zFrenteTopo]],
    [[-x, yTopo, zTrasTopo], [x, yTopo, zTrasTopo]],
    [[-x, yBase, zFrenteBase], [x, yBase, zFrenteBase]],
    [[-x, yBase, zTrasBase], [x, yBase, zTrasBase]],
  )
  return (
    <group>
      {segmentos.map((pontos, i) => <Line key={i} points={pontos} color="#c3a6ff" lineWidth={7} />)}
      <Rotulo3D posicao={[0, perfil.tetoY + 0.35, 0]} texto="ESTRUTURA ESPACIAL · rede resistente" variante="eixo" fator={5} />
    </group>
  )
}
